import React, { useState } from "react";
import PropTypes from "prop-types";
import { Card, CardText, CardImg, CardImgOverlay, Col } from "reactstrap";
import Moment from "react-moment";
import { useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  MdVolumeUp,
  MdOndemandVideo,
  MdAttachMoney,
  MdVpnLock
} from "react-icons/md";
import _ from "lodash";
import RegisterModal from "../../../commons/modal/components/register-modal";
import GeoModal from "../../../commons/modal/components/geo-modal";
import { eventSelected } from "../../../state/events/actions";
import eventPropType from "../../../prop-types/event";
import "../styles/event-card.scss";
import {
  ON_DEMAND,
  PLAYER,
  EVENT,
  PLANNED,
  VIDEO,
  AUDIO
} from "../../../constants";
import {
  getCardClassName,
  getCardImageOverlay,
  getIconContainerClassName
} from "../../../utils/style-helper";

const propTypes = {
  isGeoEnabled: PropTypes.bool.isRequired,
  item: eventPropType,
  logoUrl: PropTypes.string
};

const defaultProps = {
  item: {},
  logoUrl: ""
};

function EventCard(props) {
  const {
    item: {
      id,
      startTime,
      title,
      ppv,
      eventCardUrl,
      eventStatus,
      hasVideo,
      hasAudio,
      restricted
    },
    logoUrl,
    isGeoEnabled
  } = props;

  const [regModalOpen, setRegModalOpen] = useState(false);
  const [geoModalOpen, setGeoModalOpen] = useState(false);
  const [chosenEvent, setChosenEvent] = useState(null);
  const { loggedIn } = useSelector((state) => state.user);
  const { requireRegistration } = useSelector(
    (state) => state.client.client.features
  );
  const showRegisterModal = !loggedIn && !!requireRegistration && !ppv;
  const showGeoModal = isGeoEnabled && restricted;
  const history = useHistory();
  const location = useLocation();
  const params = useParams();

  const calendarStrings = {
    sameDay: "[Today]",
    nextDay: "[Tomorrow]"
  };

  const splitTitle = title ? title.split(" vs ") : [];
  const isLive = eventStatus === "live";
  const showDefaultBackground = !_.isEmpty(logoUrl) && _.isEmpty(eventCardUrl);

  const handleEventClick = (eventItem) => {
    if (showRegisterModal) {
      setRegModalOpen(true);
      setChosenEvent(eventItem);
    } else if (showGeoModal) {
      setGeoModalOpen(true);
    } else {
      eventSelected(eventItem);
      history.push(
        `/${params.shortName}/${PLAYER}/${EVENT}/${eventItem.id}/${eventItem.streamType}${location.search}`
      );
    }
  };

  const cardClick = () =>
    handleEventClick({
      id,
      streamType: hasVideo ? VIDEO : AUDIO,
      ppv
    });

  const iconClick = (clickFunction, eventId, streamType) => (e) => {
    e.stopPropagation();
    clickFunction({ id: eventId, streamType });
  };

  const handleKeyDown = (e) => {
    if ((e.type === "keydown" && e.keyCode === 13) || e.type === "click") {
      cardClick();
    }
  };

  return (
    <Col xs={12} sm={6} lg={4} xl={3} className="py-1 px-1">
      {showRegisterModal && (
        <RegisterModal
          isOpen={regModalOpen}
          openCloseModal={setRegModalOpen}
          event={chosenEvent}
        />
      )}
      {showGeoModal && (
        <GeoModal isOpen={geoModalOpen} openCloseModal={setGeoModalOpen} />
      )}
      <Card
        onClick={cardClick}
        className={getCardClassName(isLive, showDefaultBackground)}
        inverse
        tabIndex="0"
        onKeyDown={handleKeyDown}
      >
        {isLive && <span className="live-tag">Live</span>}
        {!showDefaultBackground && (
          <CardImg
            src={`${window.location.protocol}//${eventCardUrl}`}
            alt={`Event ${id}`}
          />
        )}
        {showDefaultBackground && (
          <div className="default-logo-container">
            <CardImg
              src={logoUrl}
              alt={`Event ${id}`}
              className="default-logo"
            />
          </div>
        )}
        <CardImgOverlay className={getCardImageOverlay(showDefaultBackground)}>
          <CardText className="event-card-title-container mb-1">
            <span>{splitTitle[0]}</span>
            {splitTitle.length === 2 && (
              <>
                &nbsp;vs&nbsp; <br /> <span>{splitTitle[1]}</span>
              </>
            )}
          </CardText>
          <CardText className="event-card-time-container d-flex">
            <span>
              <Moment calendar={calendarStrings} format="L">
                {startTime}
              </Moment>
              <Moment className="pl-2" format="LT">
                {startTime}
              </Moment>
            </span>
            <span
              className={getIconContainerClassName(
                eventStatus === ON_DEMAND || eventStatus === PLANNED
              )}
            >
              {restricted && (
                <MdVpnLock
                  aria-label="Geo Restricted Stream"
                  style={{ color: "red" }}
                />
              )}
              {ppv && (
                <MdAttachMoney
                  aria-label="Play Video Stream"
                  onClick={iconClick(handleEventClick, id, VIDEO, ppv)}
                />
              )}
              {hasVideo && (
                <MdOndemandVideo
                  aria-label="Play Video Stream"
                  className="ml-1"
                  onClick={iconClick(handleEventClick, id, VIDEO, ppv)}
                />
              )}
              {hasAudio && (
                <MdVolumeUp
                  aria-label="Play Audio Stream"
                  className="ml-1"
                  onClick={iconClick(handleEventClick, id, AUDIO, ppv)}
                />
              )}
            </span>
          </CardText>
        </CardImgOverlay>
      </Card>
    </Col>
  );
}

EventCard.propTypes = propTypes;
EventCard.defaultProps = defaultProps;

export default EventCard;
